import { useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import * as Sentry from "@sentry/react-native";
import { initializeWebSocketSession } from "../services/api";
import { socketUrl } from "../config";

export interface WebSocketEvent {
  type: string;
  message: string;
}

interface UseWebSocketEventsProps {
  socketUrl?: string;
  onOpen?: () => void;
  onClose?: () => void;
}

export const useWebSocketEvents = ({
  socketUrl: customSocketUrl,
  onOpen,
  onClose,
}: UseWebSocketEventsProps) => {
  const didUnmount = useRef(false);

  const { lastMessage, readyState, sendMessage } = useWebSocket(
    customSocketUrl || socketUrl,
    {
      share: true,
      onOpen: async () => {
        console.log("WebSocket Connected");
        const initMessage = await initializeWebSocketSession();
        sendMessage(JSON.stringify(initMessage));
        onOpen?.();
      },
      shouldReconnect: () => {
        return didUnmount.current === false;
      },
      reconnectAttempts: 10,
      reconnectInterval: 3000,
      onError: (err) => {
        let error;
        try {
          error = JSON.stringify(err);
        } catch (e: any) {
          error = err?.message ?? e.message;
        }

        const KNOWN_ISSUES = [
          "software caused connection abort",
          "unable to resolve host",
          "failed to connect to",
        ];

        let isUnhandled = true;
        for (const issue of KNOWN_ISSUES) {
          if ((err?.message || "").toLowerCase().includes(issue)) {
            isUnhandled = false;
          }
        }

        if (isUnhandled) {
          Sentry.captureException(new Error(error));
        }
      },
      onClose: () => {
        console.log("WebSocket Closed");
        onClose?.();
      },
    },
  );

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Connected",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Disconnected",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState];

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);
  return {
    lastMessage,
    connectionStatus,
    isConnected: readyState === ReadyState.OPEN,
    readyState,
    sendMessage,
  };
};
